import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';

import {cn} from '../lib/utils';
import {Icons} from '../icons';

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg',
  {
    variants: {
      variant: {
        default: 'border-toast-border bg-foreground text-foreground',
        destructive:
          'destructive group border-destructive bg-destructive text-destructive-foreground',
        success: 'border-toast-border bg-foreground text-success-foreground',
        info: 'border-toast-border bg-foreground text-info-foreground',
        warning: 'border-toast-border bg-foreground text-warning-foreground',
        error: 'border-toast-border bg-foreground text-error-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type ToastVariant = NonNullable<VariantProps<typeof toastVariants>['variant']>;

type ToastIcon = React.ComponentType<{
  className?: string;
  strokeWidth?: string | number;
}>;

const toastIcons: Record<ToastVariant, ToastIcon | null> = {
  default: null,
  destructive: Icons.circleX,
  success: Icons.check,
  info: Icons.alert,
  warning: Icons.alertTriangle,
  error: Icons.error,
};

const toastIconColors: Record<ToastVariant, string> = {
  default: '',
  destructive: 'text-destructive-foreground',
  success: 'text-success-foreground',
  info: 'text-info',
  warning: 'text-warning-foreground',
  error: 'text-chart-1',
};

const ToastCloseContext = React.createContext<(() => void) | null>(null);

interface ToastCardProps extends VariantProps<typeof toastVariants> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  onClose: () => void;
}

function ToastCard({
  title,
  description,
  action,
  variant,
  className,
  onClose,
}: ToastCardProps) {
  const Icon = toastIcons[variant ?? 'default'];

  return (
    <ToastCloseContext.Provider value={onClose}>
      <div className={cn(toastVariants({variant}), className)}>
        {Icon && (
          <Icon
            className={cn(
              'h-5 w-5 shrink-0',
              toastIconColors[variant ?? 'default'],
            )}
            strokeWidth={2.5}
          />
        )}
        <div className="grid grow">
          {title && (
            <div className="text-sm font-semibold text-accent">{title}</div>
          )}
          {description && (
            <div className="text-sm text-accent/80 group-[.destructive]:text-destructive-foreground">
              {description}
            </div>
          )}
        </div>
        {action}
        <button
          type="button"
          aria-label="Close"
          data-toast-close=""
          onClick={onClose}
          className={cn(
            'absolute right-1 top-1 rounded-md p-1 text-muted opacity-0 transition-opacity',
            'focus:opacity-100 hover:opacity-70 focus:outline-hidden focus:ring-1 group-hover:opacity-100',
            'group-[.destructive]:text-destructive-foreground/70 hover:group-[.destructive]:text-destructive-foreground focus:group-[.destructive]:ring-destructive-foreground',
          )}
        >
          <Icons.x className="h-4 w-4" />
        </button>
      </div>
    </ToastCloseContext.Provider>
  );
}

function ToastAction({
  className,
  onClick,
  ...props
}: React.ComponentProps<'button'>) {
  const closeToast = React.useContext(ToastCloseContext);

  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-accent hover:opacity-90 px-3 text-sm font-medium transition-colors',
        'focus:outline-hidden focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50',
        'group-[.destructive]:border-muted/40 group-[.destructive]:bg-destructive hover:group-[.destructive]:bg-accent/10 hover:group-[.destructive]:text-accent focus:group-[.destructive]:ring-destructive',
        className,
      )}
      onClick={(event) => {
        onClick?.(event);
        closeToast?.();
      }}
      {...props}
    />
  );
}

export {type ToastVariant, type ToastCardProps, ToastCard, ToastAction};
